import {
  AlertColor,
    Button,
    CircularProgress,
    Modal,
    Typography,
    useMediaQuery,
  } from "@mui/material";
import { globalTheme } from "../../../common/theme";
import axios from "axios";
import React, { useImperativeHandle } from "react";
import { EndUserListItem } from "./EndUserAccessories";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import Title from "../../component/Title";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { GridColumns, GridRow, GridRowId, GridSelectionModel, GridToolbarContainer } from "@mui/x-data-grid";
import { CustomTablePagination } from "../../component/CustomPagination";
import { Cancel, CheckCircle } from "@mui/icons-material";
import { EndUserFilter } from "../../../models/classes/EndUserFilter";
import { BaseControl, DropDownComboControl, DropDownComboOption, GenericFilter } from "../../component/GenericFilter";
import { renewToken } from "../../../services/AuthService";

export interface EndUserListHandle {
    Initialise: () => void;
}

export interface EndUserListProps {
    customers: DropDownComboOption[];
    regions: DropDownComboOption[],
    onClose: Function;
}

export const EndUserList = React.forwardRef(
    (props: EndUserListProps, ref: React.Ref<EndUserListHandle>) => {
      const style = { ...globalTheme() };

      const tinyScreen = useMediaQuery('(max-width:1366px)');
      const smallScreen = useMediaQuery('(max-width:1920px)');
      const normalScreen = useMediaQuery('(max-width:2560px)');

      const [controls, setControls] = React.useState<BaseControl[]>([]); //For filtering
      const [open, setOpen] = React.useState(false);
      const [loading, setLoading] = React.useState<boolean>(false);
  
      const [gridData, setGridData] = React.useState<EndUserListItem[]>([]);
      const [selectionModel, setSelectionModel] = React.useState<GridRowId[]>(() =>
        gridData.filter((r) => r.selected).map((r) => r.id),
      );
  
      const selectedEndUser = React.useRef<EndUserListItem>();

      //Grid columns
      const columns: GridColumns = [
        { field: 'endUserName', headerName: 'End User Name', flex: 5, type: 'string' },
        { field: 'endUserEng', headerName: 'End User English Name', flex: 5, type: 'string' },
        { field: 'customerName', headerName: 'Customer Name', flex: 4, type: 'string' },
        { field: 'region', headerName: 'Region', flex: 1, type: 'string' },
      ];
  
      const [searchData, setSearchData] = React.useState<any>({
        contains: true,
        searchText: "",
        customer: 0,
        region: "ALL",
        pageSize: 10,
      });
    
      //Dialogs and snacks
      const [snackSettings, setSnackSettings] =
        React.useState<SnackBarMessageProps>({
          open: false,
          message: "",
          subMessage: "",
          severity: undefined,
          onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
        });

      function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
        setSnackSettings({
          open: true,
          message: message,
          subMessage: subMessage,
          severity: colour,
          onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
        });
      }
  
      function OnSelectionChanged(model: GridSelectionModel) {
        const ids = new Set(selectionModel);
        if (model.length > 1) {
          setSelectionModel(model.filter((s) => !ids.has(s)));
          selectedEndUser.current = gridData.find(x => x.endUserId===model.filter((s) => !ids.has(s))[0]);
        } else {
          setSelectionModel(model);
          selectedEndUser.current = gridData.find(x => x.endUserId===model.filter((s) => !ids.has(s))[0]);
        }
      }
  
      function CustomToolbar() {
        return (
          <GridToolbarContainer style={{ display: 'flex' }}>
            <Button style={{ width: "120", minWidth: "100", margin: "2px" }}
              variant="contained" size="small" color="secondary" startIcon={<CheckCircle />} onClick={() => CloseModal(false)}>Select</Button>
            <div style={{ width: `calc(100% - 240)`, minWidth: "10px" }}></div>
            <Button style={{ width: "120", minWidth: "100" }}
              variant="contained" size="small" color="secondary" startIcon={<Cancel />} onClick={(e) => CloseModal(true)}>Cancel</Button>
            <div style={{ width: "10", minWidth: "10" }}></div>
          </GridToolbarContainer>
        );
      }

      function DoSearch(event: React.FormEvent<any>) {
        if (event)
          event.preventDefault();
  
        LoadData();
      }

      function LoadData() {
        setLoading(true);
		
        let filter = new EndUserFilter({
            EndUser: searchData.searchText,
            CustomerID: searchData.customer % 100000,
            Region: searchData.region,
            Reader: ""
          }
        );
  
        axios.post<EndUserListItem[]>('Api/V1/EndUser/Search', filter, {
          headers: {
            'Content-Encoding': 'gzip'
          }
        })
          .then((result) => {
            result.data.forEach(i => {
              i.id = i.endUserId;
              i.selected = false;
            });
            setGridData(result.data)
            setLoading(false);
          },
            (error) => {
              setGridData([]);
              setLoading(false);
              console.log(error);
              showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
            }
          )
          .catch((error) => {
            setLoading(false);
            console.log(error);
            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
          });
      }

      useImperativeHandle(ref, () => ({
        Initialise() {
          setOpen(true);
          renewToken();
  
          setControls([
            {
              key: 'customer',
              title: 'Customer',
              dropdownOptions: props.customers
            } as DropDownComboControl,
            {
              key: 'region',
              title: 'Region',
              dropdownOptions: props.regions
            } as DropDownComboControl
          ]);    
  
          LoadData();
          setSelectionModel([]);
        },
      }));
  
      function CloseModal(isCancel: boolean) {
        setOpen(false);
        if (isCancel)
            selectedEndUser.current = undefined;
  
        setSearchData({
          contains: true,
          searchText: "",
          customer: 0,
          region: "ALL",
          pageSize: 10
        });

        props.onClose(!isCancel, selectedEndUser);
      }
  
      return (
        <div>
          <Modal open={open} onClose={() => CloseModal(true)}>
            <div className={style.ModalMainMedium}>
              {!loading ? (
                <div className={style.ModalBody}>
                  <Title pageTitle="End User" />
  
                  <div className={style.ModalHeading}>
                    <Typography variant="h5">End User</Typography>
                  </div>
  
                  <GenericFilter controls={controls} searchData={searchData} setSearchData={setSearchData} doSearch={DoSearch} searchBoxLabel="End User Name"/>
                  <div className={style.ModalContent}>
                    <div className={style.PageContent} style={{ minHeight: "300px" }}>
                      <DataGridPro
                        getRowId={(row) => row.id}
                        rows={gridData}
                        columns={columns}
                        pageSize={searchData.pageSize}
                        onPageSizeChange={(pageSize) => setSearchData({ ...searchData, pageSize: pageSize })}
                        pagination
                        rowsPerPageOptions={[10, 30, 50, 100]}
                        loading={loading}
                        checkboxSelection
                        components={{
                          Toolbar: CustomToolbar,
                          Footer: CustomTablePagination
                        }}
                        componentsProps={{
                          footer: { rowsPerPageOptions: [10, 30, 50, 100] }
                        }}
                        selectionModel={selectionModel}
                        onSelectionModelChange={(selectionModel) => {
                          OnSelectionChanged(selectionModel)
                        }}
                        rowHeight={tinyScreen ? 20 : (smallScreen ? 24 : 28)}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: "center" }}>
                  <CircularProgress color="primary" />
                </div>
              )}
            </div>
          </Modal>
  
          <SnackBarMessage {...snackSettings} />
  
        </div>
      );
  
  
    }
  
  );
